import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getDeviceId } from "../utils/deviceId";
import InteractiveWorldMap from "../components/RegionSelector";
import iiasaIcon from "../assets/fable_logo.png";
import scenathonIcon from "../assets/scenathon_logo_short.png";


const API = process.env.REACT_APP_API_URL;

function getHomeRouteByUser(user) {
  const typeId = Number(user?.type_id || 0);

  // 1=admin, 2=organizer, 3=teamleader, 4=countryteam, 5=observer
  if (typeId === 1) return "/admin/scenathon";
  if ([2, 3, 4, 5].includes(typeId)) return "/lobby";

  return "/initial-submission";
}

const Login = () => {
  const navigate = useNavigate();
  const { login, ensureAuth, user } = useAuth();

  const [username, setUsername] = useState(""); // email
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Signing in...");
  const [preparingSession, setPreparingSession] = useState(false);

  // ✅ NUEVO: conflicto de sesión activa
  const [sessionConflict, setSessionConflict] = useState(null);

  const canSubmit = useMemo(() => {
    const emailOk = username.trim().length > 0;
    const passOk = password.length > 0;
    return emailOk && passOk && !loading;
  }, [username, password, loading]);

  // Switch loading message after 2s
  useEffect(() => {
    if (!loading) {
      setLoadingMessage("Signing in...");
      return;
    }
    const t = setTimeout(() => setLoadingMessage("Preparing your session..."), 2000);
    return () => clearTimeout(t);
  }, [loading]);

  // ✅ Si ya hay sesión (cookie refresh válida), entra directo
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const token = await ensureAuth?.();
        if (alive && token) {
          const nextRoute = getHomeRouteByUser(user);
          navigate(nextRoute, { replace: true });
        }
      } catch {
        // silencioso
      }
    })();

    return () => {
      alive = false;
    };
  }, [ensureAuth, navigate, user]);

  async function doLogin({ force = false } = {}) {
    const deviceId = getDeviceId();

    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": deviceId,
        },
        body: JSON.stringify({
          email: username.trim().toLowerCase(),
          password,
          ...(force ? { force: true } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));
      return { response, data };
    } catch (err) {
      return {
        response: null,
        data: { success: false, error: "Network error" },
      };
    }
  }

  const handleLogin = async () => {
    if (!canSubmit) return;

    setError("");
    setSessionConflict(null); // ✅ NUEVO
    setLoading(true);

    try {
      const { response, data } = await doLogin({ force: false });

      // ✅ NUEVO: si ya hay sesión activa en otro dispositivo
      if (response.status === 409 && data.code === "SESSION_ACTIVE") {
        setSessionConflict(data);
        return;
      }

      // Pending logout task: show preparing overlay and retry
      if (response.status === 409 && data.code === "PENDING_LOGOUT_TASK") {
        setPreparingSession(true);
        const retryMs = Math.max(2000, (data.retryAfterSeconds || 3) * 1000);
        await new Promise((resolve) => setTimeout(resolve, retryMs));
        // Retry once more
        const retry = await doLogin({ force: false });
        if (retry.response?.ok && retry.data?.success) {
          setPreparingSession(false);
          login({
            token: retry.data.token,
            refreshToken: retry.data.refreshToken,
            user: retry.data.user,
            csrfToken: retry.data.csrfToken,
          });
          navigate(getHomeRouteByUser(retry.data.user), { replace: true });
        } else {
          setPreparingSession(false);
          setError("Session is still being prepared. Please try again in a moment.");
        }
        return;
      }

      if (!response) {
        setError(data.error || "Network error.");
        return;
      }

      login({
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user,
        csrfToken: data.csrfToken,
      });

      navigate(getHomeRouteByUser(data.user), { replace: true });
    } catch (err) {
      console.error(err);
      setError("Server error, please try again later.");
    } finally {
      setLoading(false);
      setPreparingSession(false);
    }
  };

  // ✅ NUEVO: fuerza cierre de sesión anterior
  const handleForceLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const { response, data } = await doLogin({ force: true });

      if (!response.ok || !data.success) {
        setError(data.error || "Could not sign in.");
        return;
      }

      setSessionConflict(null);

      login({
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user,
        csrfToken: data.csrfToken,
      });

      navigate(getHomeRouteByUser(data.user), { replace: true });
    } catch (err) {
      console.error(err);
      setError("Server error, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div className="min-h-screen bg-gray-200 flex flex-col">
      {/* Header con imágenes y texto */}
      <header className="bg-white shadow-md px-4 py-6">
        <div className="max-w-[96rem] mx-auto flex items-center justify-between gap-6">
          <a 
            href="https://lab.scenathon.org" 
            className="flex-shrink-0 hover:opacity-80 transition-opacity"
            title="Left Icon Link"
          >
            <img 
              src={scenathonIcon} 
              alt="Left Icon" 
              className="w-24 h-24 object-contain"
            />
          </a>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center flex-1">
            Welcome to the Scenathon 2026-2027 Dashboard
          </h1>
          <a 
            href="https://iiasa.ac.at/models-tools-data/food-agriculture-biodiversity-land-and-energy-fable-scenathon" 
            className="flex-shrink-0 hover:opacity-80 transition-opacity"
            title="Right Icon Link"
          >
            <img 
              src={iiasaIcon} 
              alt="Right Icon" 
              className="w-24 h-24 object-contain"
            />
          </a>
        </div>
      </header>

      {/* Contenido principal */}
      <div className="flex-1 px-2 md:px-3 lg:px-4 py-8">
        <div className="max-w-[96rem] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-2 items-center min-h-[calc(100vh-12rem)]">
      
      {/* Columna izquierda: mapa */}
      <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-7">
        <InteractiveWorldMap />
      </div>

      {/* Columna derecha: login */}
      <div className="order-1 lg:order-2 lg:col-span-6 xl:col-span-5 flex justify-center">
        <div className="w-full max-w-[500px] lg:max-w-[540px] xl:max-w-[580px] bg-white rounded-2xl shadow-lg p-8 sm:p-10 md:p-12 relative lg:min-h-[440px] xl:min-h-[490px] lg:flex lg:flex-col lg:justify-center lg:overflow-y-auto">

          {/* Preparing session overlay */}
          {preparingSession && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 rounded-2xl gap-4">
              <svg
                className="animate-spin h-10 w-10 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <p className="text-gray-700 font-medium text-base">Preparing your session...</p>
              <p className="text-gray-400 text-sm">This may take a moment</p>
            </div>
          )}

          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            Sign In
          </h2>

          {error && (
            <div className="text-red-500 text-sm mb-4 text-center">{error}</div>
          )}

          {sessionConflict && (
            <div className="mb-6 rounded-xl border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm font-semibold text-gray-900">
                An active session already exists on another device.
              </p>
              <p className="text-sm text-gray-700 mt-1">
                Do you want to close that session and sign in here?
              </p>

              <div className="text-xs text-gray-600 mt-2 space-y-1">
                {sessionConflict.activeSession?.last_seen && (
                  <div>
                    Last activity:{" "}
                    <span className="font-medium">
                      {String(sessionConflict.activeSession.last_seen)}
                    </span>
                  </div>
                )}
                {sessionConflict.activeSession?.user_agent && (
                  <div className="truncate">
                    Device:{" "}
                    <span className="font-medium">
                      {sessionConflict.activeSession.user_agent}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleForceLogin}
                  disabled={loading}
                  className={`w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 rounded-lg ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  Close previous session and sign in
                </button>

                <button
                  type="button"
                  onClick={() => setSessionConflict(null)}
                  disabled={loading}
                  className={`w-full border border-gray-300 text-gray-800 font-medium py-2 rounded-lg hover:bg-gray-50 ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="relative w-full mt-4">
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={onKeyDown}
              required
              placeholder="Email"
              className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              autoComplete="email"
            />
            <label
              className="
                absolute left-0 text-gray-400 text-base
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none
              "
            >
              Email
            </label>
          </div>

          <div className="relative w-full mt-6">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
              required
              placeholder="Password"
              className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
              autoComplete="current-password"
            />
            <label
              className="
                absolute left-0 text-gray-400 text-base
                top-2 transition-all duration-200
                peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
                peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
                pointer-events-none
              "
            >
              Password
            </label>
          </div>

          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              Forgot your password?
            </button>
          </div>

          <button
            onClick={handleLogin}
            className={`w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg mb-6 mt-8 ${
              !canSubmit ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={!canSubmit}
          >
            {loading ? loadingMessage : "Sign In"}
          </button>

          <div className="text-center text-sm text-gray-900">
            <span>Don't have an account yet?</span>
            <button
              onClick={() => navigate("/registration")}
              className="text-green-500 font-semibold ml-1"
              type="button"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    </div>
      </div>

      <p className="fixed bottom-4 left-4 text-sm text-gray-600">
        © 2026 Scenathon Lab
      </p>
    </div>
  );
};

export default Login;
